import { useRouter } from "next/router";
import {
  FC,
  Fragment,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import { useCart } from "src/hooks/useCart";
import { Locale } from "lib/Intl";
import { Cart } from "src/types/ctgraphql.d";
import { formatStandardPrice } from "src/utils/ct/priceHelper";
import { useMyOrders } from "src/hooks/useMyOrders";
import { GeneralObject, localeAdapter, useSegment } from "src/hooks/useSegment";
import {
  VisitorType,
  getShortCookie,
  getLongCookie,
  setShortCookie,
  setLongCookie,
  getVisitorTypeFromOrders,
} from "src/utils/prospect-vs-customer";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { AppCtx, ViewedPageProperties } from "src/contexts/app.context";
import { useSessionChange } from "src/hooks/useSessionChange";
import currencyMap from "src/utils/currencyMap";
import { isEqual } from "lodash-es";
import { useSession } from "next-auth/client";
import { sha256Email } from "src/segment";
import Cookies from "js-cookie";
import { COOKIE_NAME } from "src/molecules/ConsentMangerBanner";
import { CategoryPreferences } from "@ht-sdks/consent-manager/types/types";
import {
  usePageViewedContext,
  usePageViewedDispatch,
} from "src/hooks/context/use-page-viewed-context";
import {
  allBrandsTranslations,
  genderTranslations,
} from "src/utils/routeTranslations";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import { logger } from "src/utils/helper/logger";

interface Props {
  locale: Locale;
}

const getProductCount = (cart: Cart) =>
  cart.lineItems.reduce(
    (count, item) => count + ((item.quantity as number) || 0),
    0
  );

const homePathPattern = new RegExp(
  `^/(${genderTranslations.join("|")})((?=\\?).*[?&]*)?$`,
  "i"
);
const brandPagePathPattern = new RegExp(
  `^/(${allBrandsTranslations.join("|")})/(.+)$`
);
const categoryPathPattern = new RegExp(
  `^/(${genderTranslations.join("|")})/(.+)$`
);

const isProductPath = (path: string) => path.startsWith("/product");
const isCheckoutPath = (path: string) => path.startsWith("/checkout");
const getCheckoutType = (path: string) =>
  path.includes("step=3") ? "purchase" : "checkout";
const getCategoryPageType = (path: string) => {
  if (path.includes("/collections/")) {
    return "collections";
  }

  if (path.includes("/search?")) {
    return "search";
  }

  return "category";
};

const getPageType = (path: string) => {
  if (homePathPattern.test(path)) {
    return "home";
  }

  if (brandPagePathPattern.test(path)) {
    return "brand";
  }

  if (categoryPathPattern.test(path)) {
    return getCategoryPageType(path);
  }

  if (isProductPath(path)) {
    return "product";
  }

  if (isCheckoutPath(path)) {
    return getCheckoutType(path);
  }

  if (path.startsWith("/cart")) {
    return "cart";
  }

  if (path.startsWith("/favorites")) {
    return "favorites";
  }

  return "other";
};

/**
 * PageViewTracker component
 * `useCart` depends on AppContext so the component needs to be a child of App
 */
const PageViewTracker: FC<Props> = ({ locale }) => {
  const { pageViewedProperties, isPrivateSale, employeeSaleData } =
    useContext(AppCtx);
  const [session] = useSession();
  const router = useRouter();
  const { segmentPageViewed } = useSegment();
  const { refetch: fetchMyOrders } = useMyOrders({
    limit: 1,
    skip: true,
  });
  const { refetchCart } = useCart({ skip: true });
  const { pageViewTracked } = usePageViewedContext();
  const pageViewedDispatch = usePageViewedDispatch();

  const isLoggedIn = useIsLoggedIn();

  const lastTrackedPath = useRef<string>();
  const lastPagedViewedProperties = useRef<GeneralObject>();
  const { currentShopType } = useShopTypeContext();
  const currency = currencyMap[locale] || "EUR";

  useSessionChange(isPrivateSale, employeeSaleData, locale);

  const setVisitorType = useCallback(async () => {
    const shortCookie = getShortCookie();
    const longCookie = getLongCookie();

    if (shortCookie) {
      setShortCookie(shortCookie);
      return;
    }

    if (isLoggedIn) {
      const { data } = await fetchMyOrders();
      const visitorType = getVisitorTypeFromOrders(data);

      setLongCookie(visitorType);
      setShortCookie(visitorType);

      return;
    }

    if (longCookie) {
      setShortCookie(longCookie);
      return;
    }

    setLongCookie(VisitorType.PROSPECT);
    setShortCookie(VisitorType.PROSPECT);
  }, [fetchMyOrders, isLoggedIn]);

  useEffect(() => {
    void setVisitorType();
  }, [setVisitorType]);

  useEffect(() => {
    const handleSetVisitorType = () => {
      void setVisitorType();
    };

    const handleRouteChangeStart = () => {
      pageViewedDispatch({ type: "SET_PAGE_VIEWED", payload: false });
    };

    const handleRouteChangeError = () => {
      pageViewedDispatch({ type: "SET_PAGE_VIEWED", payload: false });
    };

    router.events.on("routeChangeStart", handleRouteChangeStart);
    router.events.on("routeChangeError", handleRouteChangeError);
    router.events.on("routeChangeComplete", handleSetVisitorType);

    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart);
      router.events.off("routeChangeError", handleRouteChangeError);
      router.events.off("routeChangeComplete", handleSetVisitorType);
    };
  }, [router, setVisitorType, pageViewedDispatch]);

  const segmentPageViewedCb = useCallback(
    async (url: string, productAvailable?: boolean) => {
      const hashedEmail = session?.user?.email
        ? sha256Email(session?.user?.email)
        : undefined;

      // Only fetch cart data if session is available to avoid 500 errors
      let activeCart = null;
      if (session) {
        try {
          const { data: cartData } = await refetchCart();
          activeCart = cartData?.inStore?.me?.activeCart;
        } catch (error) {
          logger({
            message: "Failed to fetch cart data:",
            error: error as Error,
          });
        }
      }

      let isOldCustomer = false;
      if (session?.accessToken) {
        const { data: myOrderData } = await fetchMyOrders();
        isOldCustomer = !!myOrderData?.me?.orders?.results?.length;
      }
      const cookiePreferences = Cookies.getJSON(COOKIE_NAME) as
        | { custom?: CategoryPreferences }
        | undefined;

      const consentStatus = cookiePreferences?.custom
        ? Object.keys(cookiePreferences?.custom)
            .filter((key) => cookiePreferences.custom?.[key])
            .join("||")
        : undefined;

      const {
        collection_id,
        collection,
        brand,
        brand_id,
        category_name,
        category_id,
      } = pageViewedProperties as ViewedPageProperties;

      return await segmentPageViewed({
        pathName: url,
        locale,
        ...(activeCart && {
          cartData: {
            product_count: activeCart ? getProductCount(activeCart) : undefined,
            cart_total: formatStandardPrice(activeCart.totalPrice),
          },
        }),
        productAvailable,
        gtmProperties: {
          page_type: getPageType(url) || "other",
          page_location: window.location.href,
          shop_locale: localeAdapter(locale),
          platform: "web",
          shop_currency: currency,
          shop_segment: currentShopType || "women",
          website_name: "otrium",
          logged_in: isLoggedIn,
          user_id: session?.user?.user_id,
          eid_sha256: hashedEmail,
          consent_status: consentStatus || "functional",
          customer_type: isOldCustomer ? "old customer" : "new customer",
          collection,
          collection_id,
          brand,
          brand_id,
          category_name,
          category_id,
        },
      });
    },
    [
      session,
      locale,
      currency,
      isLoggedIn,
      pageViewedProperties,
      currentShopType,
      refetchCart,
      fetchMyOrders,
      segmentPageViewed,
    ]
  );

  const trackPageViewed = useCallback(async () => {
    const currentUrl =
      window?.location?.pathname + window?.location?.search || "";

    lastTrackedPath.current = currentUrl;

    if (
      isCheckoutPath(currentUrl) &&
      (window?.location?.search.includes("payment_id=") ||
        !window?.location?.search.includes("step="))
    ) {
      return;
    }

    await segmentPageViewedCb(currentUrl);

    pageViewedDispatch({ type: "SET_PAGE_VIEWED", payload: true });
  }, [segmentPageViewedCb, pageViewedDispatch]);

  const handleRouteChange = useCallback(
    (url: string) => {
      const checkoutQueryParams = isCheckoutPath(url)
        ? url.split("?")[1]
        : undefined;

      const currentProperties = {
        ...pageViewedProperties,
        user_id: session?.user?.user_id,
        ...(isCheckoutPath(url) && {
          step: checkoutQueryParams?.split("=")[1] || 0,
        }),
      };

      const isProductPageNavigated =
        lastPagedViewedProperties.current?.page_type === "product" &&
        url.includes("/product") &&
        lastTrackedPath.current !== url;

      const isSamePageProperties = isEqual(
        lastPagedViewedProperties.current,
        currentProperties
      );

      const shouldNotLoad = isProductPageNavigated
        ? false
        : lastTrackedPath.current === url ||
          isSamePageProperties ||
          currentProperties.step === 0 ||
          (url.includes("/checkout") && !url.includes("?payment_id"));

      if (shouldNotLoad) {
        return;
      }

      lastTrackedPath.current = url;
      lastPagedViewedProperties.current = currentProperties;
    },
    [pageViewedProperties, session?.user?.user_id]
  );

  useEffect(() => {
    const currentUrl =
      window?.location?.pathname + window?.location?.search || "";

    if (lastTrackedPath.current === currentUrl || pageViewTracked) {
      return;
    }

    void trackPageViewed();
  }, [trackPageViewed, pageViewTracked]);

  useEffect(() => {
    if (!session) {
      return;
    }

    if (pageViewedProperties && Object.keys(pageViewedProperties).length > 0) {
      handleRouteChange(router.asPath);
    } else {
      router.events.on("routeChangeComplete", handleRouteChange);
    }

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router, pageViewedProperties, session, handleRouteChange]);

  return <Fragment />;
};

export default PageViewTracker;
